import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useUser } from "@clerk/clerk-react";
import { getUserProgress, updateLessonProgress } from "../data/database";
import SaveButton from "../components/SaveButton";

const conversationalPhrases = [
  { word: "صباح الخير", pronunciation: "Ṣabāḥ al-khayr", meaning: "Good morning" },
  { word: "مساء الخير", pronunciation: "Masāʾ al-khayr", meaning: "Good evening" },
  { word: "تصبح على خير", pronunciation: "Tuṣbiḥ ʿalā khayr", meaning: "Good night" },
  { word: "مع السلامة", pronunciation: "Maʿa as-salāmah", meaning: "Goodbye" },
  { word: "كيف حالك؟", pronunciation: "Kayfa ḥāluka / ḥāluki", meaning: "How are you?" },
  { word: "أنا بخير، شكراً", pronunciation: "Anā bikhayr, shukran", meaning: "I'm fine, thank you" },
  { word: "ما اسمك؟", pronunciation: "Mā ismuka / ismuki?", meaning: "What is your name?" },
  { word: "اسمي...", pronunciation: "Ismī...", meaning: "My name is..." },
  { word: "من أين أنت؟", pronunciation: "Min ayna anta / anti?", meaning: "Where are you from?" },
  { word: "أنا من...", pronunciation: "Anā min...", meaning: "I'm from..." },
  { word: "تشرفت بلقائك", pronunciation: "Tasharraftu biliqāʾik", meaning: "Nice to meet you" },
  { word: "هل تتكلم الإنجليزية؟", pronunciation: "Hal tatakallam al-inglīziyyah?", meaning: "Do you speak English?" },
  { word: "أنا لا أفهم", pronunciation: "Anā lā afham", meaning: "I don't understand" },
  { word: "من فضلك", pronunciation: "Min faḍlik", meaning: "Please" },
  { word: "عفواً", pronunciation: "ʿAfwan", meaning: "You're welcome / Excuse me" },
  { word: "آسف", pronunciation: "Āsif", meaning: "Sorry" }
];

function ConversationalLesson() {
  const { user } = useUser();
  const [selectedWord, setSelectedWord] = useState(null);
  const [clickedWords, setClickedWords] = useState(new Set());

  // Load progress from database on mount
  useEffect(() => {
    const loadProgress = () => {
      if (user) {
        const userProgress = getUserProgress(user.id);
        const learnedCount = userProgress.conversation || 0;
        if (learnedCount > 0) {
          setClickedWords(new Set(conversationalPhrases.slice(0, learnedCount).map(w => w.word)));
        }
      }
    };
    loadProgress();
  }, [user]);

  const handleClick = async (wordObj) => {
    setSelectedWord(wordObj);
    const updated = new Set(clickedWords).add(wordObj.word);
    setClickedWords(updated);
    
    // Save progress to database
    if (user) {
      try {
        await updateLessonProgress(user.id, 'conversation', updated.size);
        console.log('Progress saved to database:', updated.size);
      } catch (error) {
        console.error('Error saving progress to database:', error);
      }
    }
    window.responsiveVoice.speak(wordObj.word, "Arabic Male");
  };

  const progress = (clickedWords.size / conversationalPhrases.length) * 100;

  return (
    <div dir="rtl" className="p-4 sm:p-6 text-center font-arabic h-full bg-gradient-to-br from-yellow-50 to-white overflow-y-auto">
      <h2 className="text-3xl sm:text-4xl font-bold mb-6 text-yellow-800">عبارات شائعة للمحادثة</h2>

      {/* Progress Bar */}
      <div className="w-full max-w-md mx-auto bg-gray-200 rounded-full h-4 mb-4 overflow-hidden shadow-inner">
        <motion.div
          className="bg-green-500 h-full"
          initial={{ width: 0 }}
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.5 }}
        />
      </div>
      <p className="text-sm text-gray-600 mb-6">تم تعلم {clickedWords.size} من {conversationalPhrases.length} عبارة</p>

      <AnimatePresence>
        {selectedWord && (
          <motion.div
            key="word-info"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.3 }}
            className="text-lg sm:text-xl text-green-700 mb-6"
          >
            <p>النطق: <strong>{selectedWord.pronunciation}</strong></p>
            <p>المعنى: <strong>{selectedWord.meaning}</strong></p>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 justify-center mb-6">
        {conversationalPhrases.map((wordObj, index) => {
          const learned = clickedWords.has(wordObj.word);
          return (
            <button
              key={index}
              onClick={() => handleClick(wordObj)}
              className={`text-xl font-semibold p-4 rounded-2xl shadow-md transition duration-200 text-wrap whitespace-pre-wrap break-words ${
                learned ? "bg-green-100 text-green-700" : "bg-white text-yellow-800 hover:bg-yellow-100"
              }`}
            >
              {wordObj.word}
            </button>
          );
        })}
      </div>

      {/* Save Button */}
      <div className="flex justify-center mb-6">
        <SaveButton
          lessonType="conversation"
          progressData={clickedWords.size}
        />
      </div>

      {/* ResponsiveVoice Script */}
      <script src="https://code.responsivevoice.org/responsivevoice.js?key=1X64heoV"></script>
    </div>
  );
}

export default ConversationalLesson;
